import { AnyAction, Dispatch, Middleware, PayloadAction, ThunkDispatch } from "@reduxjs/toolkit";

// Import reset actions from each slice
import { resetCourses, fetchCourses } from "../reducers/courses/courseSlice";
import { resetTask, fetchTasks } from "../reducers/task/taskSlice";
import { resetResources, fetchResources } from "../reducers/resources/resourceSlice";
import { resetTemplates, fetchTemplates } from "../reducers/qa/qaSlice";
import { resetSchedules, fetchSchedules } from "../reducers/schedule/scheduleSlice";
import { resetOrganizations, fetchOrganizationTutors, fetchOrganizations } from "../reducers/organization/organizationSlice";
import { resetUsers } from "../reducers/users/usersSlice";
import { resetDepartments } from "../reducers/departments/departmentSlice";
import { AppDispatch } from "./store";

// Define interfaces for better type safety
interface User {
  role: string;
  [key: string]: any; // Allow for other user properties
}

interface AuthState {
  user: User | null;
  [key: string]: any; // Allow for other auth properties
}

interface RootState {
  auth: AuthState;
  [key: string]: any; // Allow for other state slices
}

interface QueryParams {
  page: number;
  limit: number;
  isPublished: boolean;
}

const queryParams: QueryParams = {
  page: 1,
  limit: 10,
  isPublished: true,
};


// Reset all slices helper function
export const resetAllSlices = (dispatch: AppDispatch): void => {
  dispatch(resetCourses());
  dispatch(resetTask());
  dispatch(resetResources());
  dispatch(resetTemplates());
  dispatch(resetSchedules());
  dispatch(resetOrganizations());
  dispatch(resetUsers());
  dispatch(resetDepartments());
};

// Reset essential slices (the missing function that was referenced)
export const resetEssentialSlices = (dispatch: AppDispatch): void => {
  dispatch(resetCourses());
  dispatch(resetTask());
  dispatch(resetResources());
  dispatch(resetTemplates());
  dispatch(resetSchedules());
  dispatch(resetOrganizations());
};

// Refetch essential data (backend handles role-based filtering via cookies)
export const refetchEssentialData = (dispatch: AppDispatch): void => {
  dispatch(fetchCourses(queryParams));  // Backend filters based on user role in cookies
  dispatch(fetchTasks({}));    // Backend filters based on user role in cookies  
  dispatch(fetchSchedules({})); // Backend filters based on user role in cookies
};

// Type guard to check if action has a type property
const hasType = (action: unknown): action is { type: string } => {
  return typeof action === 'object' && action !== null && 'type' in action;
};

// Redux middleware to handle role changes
export const roleChangeMiddleware: Middleware<{}, RootState> = (store) => (next) => (action: unknown) => {
  // Type guard to ensure action has required properties
  if (!hasType(action)) {
    return next(action);
  }

  // Get the current state before the action is processed
  const prevState = store.getState();
  const prevRole = prevState.auth?.user?.role;

  // Let the action pass through first
  const result = next(action);

  // Get the new state after the action
  const nextState = store.getState();
  const nextRole = nextState.auth?.user?.role;

  // Check if this is a role change (and both roles exist to avoid initial load triggers)
  if (prevRole && nextRole && prevRole !== nextRole) {

    // Reset essential slices to clear previous role's data
    resetEssentialSlices(store.dispatch);

    // Refetch data (backend will filter based on new role in cookies)
    refetchEssentialData(store.dispatch);
  }

  return result;
};

// Alternative approach: Listen for login/auth actions
export const authActionMiddleware: Middleware<{}, RootState> = (store) => (next) => (action: unknown) => {
  // Type guard to ensure action has required properties
  if (!hasType(action)) {
    return next(action);
  }

  const result = next(action);

  // Listen for login or role change actions
  if (action.type === 'auth/loginSuccess' ||
    action.type === 'auth/setUserRole' ||
    action.type === 'auth/updateUserRole') {

    const state = store.getState();
    const currentRole = state.auth?.user?.role;

    if (currentRole) {

      // Reset essential slices
      resetEssentialSlices(store.dispatch);

      // Refetch data (backend handles role-based filtering)
      refetchEssentialData(store.dispatch);
    }
  }

  return result;
};